import React, { useState, useEffect } from 'react';
import { Building2, Plus, Eye, EyeOff, Trash2, AlertCircle, CreditCard, Wallet, ArrowUpRight, ArrowDownRight, Download, Bell, Filter, Calendar, TrendingUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { encryptData, decryptData } from '../../lib/encryption';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { SearchInput } from '../common/SearchInput';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface BankAccount {
  id: string;
  type: string;
  institution: string;
  account_number_encrypted?: string;
  routing_number_encrypted?: string;
  balance?: string;
  currency: string;
  statements_location?: string;
  notes?: string;
  category?: string;
  alerts?: boolean;
}

interface Transaction {
  date: string;
  description: string;
  amount: number;
  type: 'credit' | 'debit';
  category?: string;
}

export function BankAccounts() {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSensitiveInfo, setShowSensitiveInfo] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [view, setView] = useState<'cards' | 'list'>('cards');
  const [showTransactionFilters, setShowTransactionFilters] = useState(false);
  const [transactionFilters, setTransactionFilters] = useState({
    dateRange: 'all',
    type: 'all',
    category: 'all'
  });

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [balanceHistory, setBalanceHistory] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    type: 'checking',
    institution: '',
    account_number: '',
    routing_number: '',
    balance: '',
    currency: 'AUD',
    statements_location: '',
    notes: ''
  });

  const accountTypes = [
    { value: 'checking', label: 'Checking Account' },
    { value: 'savings', label: 'Savings Account' },
    { value: 'credit', label: 'Credit Card' },
    { value: 'investment', label: 'Investment Account' },
    { value: 'retirement', label: 'Retirement Account' },
    { value: 'loan', label: 'Loan' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (user) {
      loadAccounts();
      loadTransactions();
    }
  }, [user]);

  useEffect(() => {
    const handler = () => { if (user) { loadAccounts(true); loadTransactions(); } };
    window.addEventListener('refresh-financial', handler);
    return () => window.removeEventListener('refresh-financial', handler);
  }, [user]);

  const loadTransactions = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('financial_transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false })
        .limit(50);

      if (error) throw error;
      
      // Transform to expected format
      const transformedTransactions = data?.map(t => ({
        date: t.date,
        description: t.description,
        amount: Math.abs(parseFloat(t.amount || '0')),
        type: t.transaction_type === 'deposit' ? 'credit' : 'debit',
        category: t.category || 'Other'
      })) || [];
      
      setTransactions(transformedTransactions);
    } catch (error) {
      console.error('Error loading transactions:', error);
    }
  };

  async function loadAccounts(background = false) {
    try {
      if (!background) setLoading(true);
      const { data, error } = await supabase
        .from('financial_accounts')
        .select('*')
        .eq('user_id', user?.id)
        .in('type', ['checking', 'savings']);

      if (error) throw error;
      setAccounts(data || []);

      // If there are accounts, select the first one by default
      if (data && data.length > 0) {
        setSelectedAccount(data[0]);
      }
    } catch (err) {
      if (!background) setError(err instanceof Error ? err.message : 'Failed to load accounts');
    } finally {
      if (!background) setLoading(false);
    }
  }

  const handleSaveAccount = async () => {
    try {
      setError(null);
      
      // Validate required fields
      if (!formData.institution.trim()) {
        setError('Institution name is required');
        return;
      }

      // Prepare account data with proper types
      const accountData: any = {
        type: formData.type,
        institution: formData.institution.trim(),
        balance: formData.balance ? parseFloat(formData.balance) : 0,
        currency: formData.currency,
        statements_location: formData.statements_location || null,
        notes: formData.notes || null,
        user_id: user?.id
      };

      // Only encrypt if account number is provided
      if (formData.account_number.trim()) {
        try {
          accountData.account_number_encrypted = await encryptData(formData.account_number.trim());
        } catch (encryptError) {
          console.error('Encryption error:', encryptError);
          setError('Failed to encrypt account number. Please try again.');
          return;
        }
      }

      // Only encrypt if routing number is provided
      if (formData.routing_number.trim()) {
        try {
          accountData.routing_number_encrypted = await encryptData(formData.routing_number.trim());
        } catch (encryptError) {
          console.error('Encryption error:', encryptError);
          setError('Failed to encrypt routing number. Please try again.');
          return;
        }
      }

      console.log('Saving account data:', { ...accountData, account_number_encrypted: '***', routing_number_encrypted: '***' });

      const { data, error } = await supabase
        .from('financial_accounts')
        .insert([accountData])
        .select()
        .single();

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      console.log('Account saved successfully:', data);

      setAccounts(prev => [...prev, data]);
      setSelectedAccount(data);
      setShowAddModal(false);
      setFormData({
        type: 'checking',
        institution: '',
        account_number: '',
        routing_number: '',
        balance: '',
        currency: 'AUD',
        statements_location: '',
        notes: ''
      });
    } catch (err) {
      console.error('Save account error:', err);
      setError(err instanceof Error ? err.message : 'Failed to save account');
    }
  };

  const handleDeleteAccount = async (id: string) => {
    try {
      const { error } = await supabase
        .from('financial_accounts')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setAccounts(prev => prev.filter(account => account.id !== id));
      if (selectedAccount?.id === id) {
        setSelectedAccount(accounts.length > 1 ? accounts.find(a => a.id !== id) || null : null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete account');
    }
  };

  const toggleSensitiveInfo = (accountId: string) => {
    setShowSensitiveInfo(prev => ({
      ...prev,
      [accountId]: !prev[accountId]
    }));
  };

  const getTotalBalance = () => {
    return accounts.reduce((total, account) => {
      const balance = parseFloat(account.balance || '0');
      return total + balance;
    }, 0);
  };

  const getAccountIcon = (type: string) => {
    switch (type) {
      case 'credit':
        return <CreditCard className="text-[#1E1B4B]" size={24} />;
      case 'savings':
        return <Wallet className="text-[#1E1B4B]" size={24} />;
      default:
        return <Building2 className="text-[#1E1B4B]" size={24} />;
    }
  };

  const filteredAccounts = accounts.filter(account => {
    return account.institution.toLowerCase().includes(searchQuery.toLowerCase()) ||
           account.type.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleExportTransactions = () => {
    // In a real app, this would export to CSV/PDF
    alert('Export functionality would be implemented here');
  };

  const handleSetAlert = async (accountId: string, enabled: boolean) => {
    try {
      const { error } = await supabase
        .from('financial_accounts')
        .update({ alerts: enabled })
        .eq('id', accountId);

      if (error) throw error;

      setAccounts(prev => prev.map(account => 
        account.id === accountId ? { ...account, alerts: enabled } : account
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update alert settings');
    }
  };

  const filteredTransactions = transactions.filter(transaction => {
    if (transactionFilters.dateRange !== 'all') {
      // Add date filtering logic
    }
    if (transactionFilters.type !== 'all' && transaction.type !== transactionFilters.type) {
      return false;
    }
    if (transactionFilters.category !== 'all' && transaction.category !== transactionFilters.category) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/4"></div>
        <div className="h-32 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bank Accounts</h1>
          <p className="text-gray-600 mt-1">Manage your bank accounts and track balances</p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowAddModal(true)}
          icon={<Plus size={20} />}
        >
          Add Account
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="text-red-500" size={20} />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-idecide-primary to-idecide-secondary text-idecide-dark">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-2">Total Balance</h3>
            <p className="text-3xl font-bold">${getTotalBalance().toLocaleString()}</p>
            <p className="text-sm mt-2 text-idecide-dark/80">Across all accounts</p>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-idecide-primary to-idecide-secondary text-idecide-dark">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-2">Active Accounts</h3>
            <p className="text-3xl font-bold">{accounts.length}</p>
            <p className="text-sm mt-2 text-idecide-dark/80">Total accounts managed</p>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-idecide-primary to-idecide-secondary text-idecide-dark">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-2">Last Updated</h3>
            <p className="text-xl font-bold">Today</p>
            <p className="text-sm mt-2 text-idecide-dark/80">All accounts up to date</p>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Accounts List */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-idecide-primary shadow-sm">
          <div className="p-4 border-b border-idecide-primary flex justify-between items-center">
            <h2 className="text-lg font-semibold">Your Accounts</h2>
            <div className="flex gap-2">
              <SearchInput
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search accounts..."
                className="w-64"
              />
              <Button
                variant="outline"
                onClick={() => setView(view === 'cards' ? 'list' : 'cards')}
              >
                {view === 'cards' ? 'List View' : 'Card View'}
              </Button>
            </div>
          </div>

          <div className="p-4">
            {filteredAccounts.length === 0 ? (
              <div className="text-center py-8">
                <Building2 className="mx-auto text-gray-400" size={48} />
                <h3 className="mt-4 text-lg font-medium text-gray-900">No accounts yet</h3>
                <p className="mt-2 text-gray-500">Add your first bank account to get started</p>
                <Button
                  variant="primary"
                  onClick={() => setShowAddModal(true)}
                  className="mt-4"
                >
                  Add Account
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredAccounts.map((account) => (
                  <div
                    key={account.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setSelectedAccount(account)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getAccountIcon(account.type)}
                        <div>
                          <h3 className="font-semibold text-gray-900">{account.institution}</h3>
                          <p className="text-sm text-gray-500 capitalize">{account.type} Account</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">
                          ${parseFloat(account.balance || '0').toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-500">{account.currency}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Account Details */}
        <div className="bg-white rounded-xl border border-idecide-primary shadow-sm">
          <div className="p-4 border-b border-idecide-primary">
            <h2 className="text-lg font-semibold">Account Details</h2>
          </div>
          <div className="p-4">
            {selectedAccount ? (
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900">{selectedAccount.institution}</h3>
                  <p className="text-sm text-gray-500 capitalize">{selectedAccount.type} Account</p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-gray-900">
                    ${parseFloat(selectedAccount.balance || '0').toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500">Current Balance</p>
                </div>

                {selectedAccount.notes && (
                  <div>
                    <h4 className="font-medium text-gray-900 mb-1">Notes</h4>
                    <p className="text-sm text-gray-600">{selectedAccount.notes}</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSetAlert(selectedAccount.id, !selectedAccount.alerts)}
                  >
                    <Bell size={16} />
                    {selectedAccount.alerts ? 'Disable Alerts' : 'Enable Alerts'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteAccount(selectedAccount.id)}
                  >
                    <Trash2 size={16} />
                    Delete
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>Select an account to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Account Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add Bank Account"
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Type *
              </label>
              <select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-idecide-primary"
              >
                {accountTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Currency
              </label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-idecide-primary"
              >
                <option value="AUD">AUD</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Institution Name *
            </label>
            <input
              type="text"
              value={formData.institution}
              onChange={(e) => setFormData(prev => ({ ...prev, institution: e.target.value }))}
              placeholder="e.g., Commonwealth Bank, ANZ"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-idecide-primary"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Number
              </label>
              <input
                type="text"
                value={formData.account_number}
                onChange={(e) => setFormData(prev => ({ ...prev, account_number: e.target.value }))}
                placeholder="Account number"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-idecide-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Routing Number
              </label>
              <input
                type="text"
                value={formData.routing_number}
                onChange={(e) => setFormData(prev => ({ ...prev, routing_number: e.target.value }))}
                placeholder="BSB/Routing number"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-idecide-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Balance
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.balance}
              onChange={(e) => setFormData(prev => ({ ...prev, balance: e.target.value }))}
              placeholder="0.00"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-idecide-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Statements Location
            </label>
            <input
              type="text"
              value={formData.statements_location}
              onChange={(e) => setFormData(prev => ({ ...prev, statements_location: e.target.value }))}
              placeholder="e.g., /Documents/Bank Statements"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-idecide-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any additional notes about this account"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-idecide-primary"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowAddModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveAccount}
            >
              Save Account
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}